import React, { useState, useEffect } from 'react';
import {
  Insurance,
  CreateInsuranceData,
  getAllInsurances,
  createInsurance,
  updateInsurance,
  toggleInsuranceStatus,
  deleteInsurance
} from '../../services/insuranceService';
import {
  SupplementaryInsurance,
  CreateSupplementaryInsuranceData,
  getAllSupplementaryInsurances,
  createSupplementaryInsurance,
  updateSupplementaryInsurance,
  toggleSupplementaryInsuranceStatus,
  deleteSupplementaryInsurance
} from '../../services/supplementaryInsuranceService';

const InsuranceManagement: React.FC = () => {
  const [insurances, setInsurances] = useState<Insurance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newInsurance, setNewInsurance] = useState<CreateInsuranceData>({
    name: '',
    active: true,
    requiresExtraCode: false,
    requiresInsuranceBooklet: false
  });

  const [editingInsurance, setEditingInsurance] = useState<string | null>(null);
  const [showInsuranceForm, setShowInsuranceForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Supplementary Insurance state
  const [supplementaryInsurances, setSupplementaryInsurances] = useState<SupplementaryInsurance[]>([]);
  const [supplementaryLoading, setSupplementaryLoading] = useState(true);
  const [supplementaryError, setSupplementaryError] = useState<string | null>(null);
  const [newSupplementaryInsurance, setNewSupplementaryInsurance] = useState<CreateSupplementaryInsuranceData>({
    name: '',
    active: true
  });
  const [editingSupplementaryInsurance, setEditingSupplementaryInsurance] = useState<string | null>(null);
  const [showSupplementaryInsuranceForm, setShowSupplementaryInsuranceForm] = useState(false);
  const [supplementarySubmitting, setSupplementarySubmitting] = useState(false);

  // Load insurances on component mount
  useEffect(() => {
    loadInsurances();
    loadSupplementaryInsurances();
  }, []);

  const loadInsurances = async () => {
    try {
      setLoading(true);
      setError(null);
      const insuranceList = await getAllInsurances();
      setInsurances(insuranceList);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطا در بارگذاری بیمه ها');

    } finally {
      setLoading(false);
    }
  };

  const loadSupplementaryInsurances = async () => {
    try {
      setSupplementaryLoading(true);
      setSupplementaryError(null);
      const supplementaryInsuranceList = await getAllSupplementaryInsurances();
      setSupplementaryInsurances(supplementaryInsuranceList);
    } catch (err) {
      setSupplementaryError(err instanceof Error ? err.message : 'خطا در بارگذاری بیمه های تکمیلی');

    } finally {
      setSupplementaryLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    if (editingInsurance !== null) {
      // Update the insurance being edited
      setInsurances(insurances.map(ins => 
        ins._id === editingInsurance 
          ? { 
              ...ins, 
              [name]: type === 'checkbox' ? checked : value === 'true' ? true : value === 'false' ? false : value
            }
          : ins
      ));
    } else {
      // Update the new insurance
      setNewInsurance({
        ...newInsurance,
        [name]: type === 'checkbox' ? checked : value === 'true' ? true : value === 'false' ? false : value
      } as CreateInsuranceData);
    }
  };

  const addOrUpdateInsurance = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (submitting) return;
    
    try {
      setSubmitting(true);
      setError(null);
      
      if (editingInsurance !== null) {
        // Update existing insurance
        const insuranceToUpdate = insurances.find(ins => ins._id === editingInsurance);
        if (insuranceToUpdate) {
          const updatedInsurance = await updateInsurance(editingInsurance, {
            name: insuranceToUpdate.name,
            active: insuranceToUpdate.active,
            requiresExtraCode: insuranceToUpdate.requiresExtraCode,
            requiresInsuranceBooklet: insuranceToUpdate.requiresInsuranceBooklet
          });
          
          setInsurances(insurances.map(ins => 
            ins._id === editingInsurance ? updatedInsurance : ins
          ));
        }
        setEditingInsurance(null);
      } else {
        // Add new insurance
        if (!newInsurance.name.trim()) {
          alert('لطفاً نام بیمه را وارد کنید.');
          return;
        }
        
        const createdInsurance = await createInsurance(newInsurance);
        setInsurances([...insurances, createdInsurance]);
        
        setNewInsurance({
          name: '',
          active: true,
          requiresExtraCode: false,
          requiresInsuranceBooklet: false
        });
      }
      
      setShowInsuranceForm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطا در ذخیره بیمه');

    } finally {
      setSubmitting(false);
    }
  };

  const startEditing = (id: string) => {
    const insuranceToEdit = insurances.find(ins => ins._id === id);
    if (insuranceToEdit) {
      setEditingInsurance(id);
      setShowInsuranceForm(true);
    }
  };

  const cancelEditing = () => {
    setEditingInsurance(null);
    setShowInsuranceForm(false);
    setNewInsurance({
      name: '',
      active: true,
      requiresExtraCode: false,
      requiresInsuranceBooklet: false
    });
    // Reload insurances to reset any unsaved changes
    loadInsurances();
  };

  const handleToggleStatus = async (id: string) => {
    try {
      setError(null);
      const updatedInsurance = await toggleInsuranceStatus(id);
      setInsurances(insurances.map(ins => 
        ins._id === id ? updatedInsurance : ins
      ));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطا در تغییر وضعیت بیمه');

    }
  };

  const handleDeleteInsurance = async (id: string) => {
    if (!window.confirm('آیا از حذف این بیمه اطمینان دارید؟')) {
      return;
    }
    
    try {
      setError(null);
      await deleteInsurance(id);
      setInsurances(insurances.filter(ins => ins._id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطا در حذف بیمه');

    }
  };

  // Supplementary Insurance Handlers
  const handleSupplementaryInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    if (editingSupplementaryInsurance !== null) {
      // Update the supplementary insurance being edited
      setSupplementaryInsurances(supplementaryInsurances.map(ins => 
        ins._id === editingSupplementaryInsurance 
          ? { 
              ...ins, 
              [name]: type === 'checkbox' ? checked : value === 'true' ? true : value === 'false' ? false : value
            }
          : ins
      ));
    } else {
      // Update the new supplementary insurance
      setNewSupplementaryInsurance({
        ...newSupplementaryInsurance,
        [name]: type === 'checkbox' ? checked : value === 'true' ? true : value === 'false' ? false : value
      } as CreateSupplementaryInsuranceData);
    }
  };

  const addOrUpdateSupplementaryInsurance = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (supplementarySubmitting) return;
    
    try {
      setSupplementarySubmitting(true);
      setSupplementaryError(null);
      
      if (editingSupplementaryInsurance !== null) {
        // Update existing supplementary insurance
        const supplementaryInsuranceToUpdate = supplementaryInsurances.find(ins => ins._id === editingSupplementaryInsurance);
        if (supplementaryInsuranceToUpdate) {
          const updatedSupplementaryInsurance = await updateSupplementaryInsurance(editingSupplementaryInsurance, {
            name: supplementaryInsuranceToUpdate.name,
            active: supplementaryInsuranceToUpdate.active
          });
          
          setSupplementaryInsurances(supplementaryInsurances.map(ins => 
            ins._id === editingSupplementaryInsurance ? updatedSupplementaryInsurance : ins
          ));
        }
        setEditingSupplementaryInsurance(null);
      } else {
        // Add new supplementary insurance
        if (!newSupplementaryInsurance.name.trim()) {
          alert('لطفاً نام بیمه تکمیلی را وارد کنید.');
          return;
        }
        
        const createdSupplementaryInsurance = await createSupplementaryInsurance(newSupplementaryInsurance);
        setSupplementaryInsurances([...supplementaryInsurances, createdSupplementaryInsurance]);
        
        setNewSupplementaryInsurance({
          name: '',
          active: true
        });
      }
      
      setShowSupplementaryInsuranceForm(false);
    } catch (err) {
      setSupplementaryError(err instanceof Error ? err.message : 'خطا در ذخیره بیمه تکمیلی');

    } finally {
      setSupplementarySubmitting(false);
    }
  };

  const startEditingSupplementaryInsurance = (id: string) => {
    const supplementaryInsuranceToEdit = supplementaryInsurances.find(ins => ins._id === id);
    if (supplementaryInsuranceToEdit) {
      setEditingSupplementaryInsurance(id);
      setShowSupplementaryInsuranceForm(true);
    }
  };

  const cancelEditingSupplementaryInsurance = () => {
    setEditingSupplementaryInsurance(null);
    setShowSupplementaryInsuranceForm(false);
    setNewSupplementaryInsurance({
      name: '',
      active: true
    });
    // Reload supplementary insurances to reset any unsaved changes
    loadSupplementaryInsurances();
  };

  const handleToggleSupplementaryInsuranceStatus = async (id: string) => {
    try {
      setSupplementaryError(null);
      const updatedSupplementaryInsurance = await toggleSupplementaryInsuranceStatus(id);
      setSupplementaryInsurances(supplementaryInsurances.map(ins => 
        ins._id === id ? updatedSupplementaryInsurance : ins
      ));
    } catch (err) {
      setSupplementaryError(err instanceof Error ? err.message : 'خطا در تغییر وضعیت بیمه تکمیلی');

    }
  };

  const handleDeleteSupplementaryInsurance = async (id: string) => {
    if (!window.confirm('آیا از حذف این بیمه تکمیلی اطمینان دارید؟')) {
      return;
    }
    
    try {
      setSupplementaryError(null);
      await deleteSupplementaryInsurance(id);
      setSupplementaryInsurances(supplementaryInsurances.filter(ins => ins._id !== id));
    } catch (err) {
      setSupplementaryError(err instanceof Error ? err.message : 'خطا در حذف بیمه تکمیلی');

    }
  };

  const currentInsurance = editingInsurance !== null
    ? insurances.find(ins => ins._id === editingInsurance)
    : newInsurance;

  const currentSupplementaryInsurance = editingSupplementaryInsurance !== null
    ? supplementaryInsurances.find(ins => ins._id === editingSupplementaryInsurance)
    : newSupplementaryInsurance;

  if (loading || supplementaryLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-center items-center h-64">
            <div className="text-lg text-gray-600">در حال بارگذاری بیمه ها...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">مدیریت بیمه های پایه</h1>
          <button 
            onClick={() => {
              setEditingInsurance(null);
              setShowInsuranceForm(!showInsuranceForm);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {showInsuranceForm && !editingInsurance ? 'انصراف' : 'افزودن بیمه جدید'}
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
            <span className="block sm:inline">{error}</span>
            <button
              onClick={() => setError(null)}
              className="absolute top-0 bottom-0 left-0 px-4 py-3"
            >
              <span className="sr-only">بستن</span>
              ✕
            </button>
          </div>
        )}
        
        {showInsuranceForm && (
          <div className="mb-8 bg-gray-50 p-6 rounded-lg">
            <h2 className="text-lg font-semibold mb-4">
              {editingInsurance !== null ? 'ویرایش بیمه' : 'افزودن بیمه جدید'}
            </h2>
            <form onSubmit={addOrUpdateInsurance}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">نام بیمه</label>
                  <input
                    type="text"
                    name="name"
                    value={currentInsurance?.name || ''}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="نام بیمه را وارد کنید"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">وضعیت</label>
                  <select
                    name="active"
                    value={currentInsurance?.active ? 'true' : 'false'}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="true">فعال</option>
                    <option value="false">غیرفعال</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">نیاز به کد رهگیری</label>
                  <select
                    name="requiresExtraCode"
                    value={currentInsurance?.requiresExtraCode ? 'true' : 'false'}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="false">خیر</option>
                    <option value="true">بله</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    آیا این بیمه نیاز به کد رهگیری برای نسخه الکترونیک دارد؟
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">نیاز به تصویر دفترچه</label>
                  <select
                    name="requiresInsuranceBooklet"
                    value={currentInsurance?.requiresInsuranceBooklet ? 'true' : 'false'}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="false">خیر</option>
                    <option value="true">بله</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    آیا این بیمه نیاز به آپلود تصویر دفترچه بیمه دارد؟
                  </p>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-2">
                {editingInsurance !== null && (
                  <button
                    type="button"
                    onClick={cancelEditing}
                    className="ml-2 px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    انصراف
                  </button>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
                >
                  {submitting 
                    ? 'در حال ذخیره...' 
                    : editingInsurance !== null 
                      ? 'به‌روزرسانی بیمه' 
                      : 'افزودن بیمه'
                  }
                </button>
              </div>
            </form>
          </div>
        )}
        
        {insurances.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-500 text-lg">هیچ بیمه‌ای یافت نشد.</div>
            <button
              onClick={() => setShowInsuranceForm(true)}
              className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
            >
              افزودن اولین بیمه
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {insurances.map(insurance => (
              <div 
                key={insurance._id} 
                className={`border rounded-lg shadow-sm overflow-hidden ${insurance.active ? '' : 'opacity-60'}`}
              >
                <div className="bg-gray-100 p-4">
                  <div className="flex justify-between items-start">
                    <h2 className="text-lg font-semibold">{insurance.name}</h2>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      insurance.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {insurance.active ? 'فعال' : 'غیرفعال'}
                    </span>
                  </div>
                </div>
                
                <div className="p-4">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">نیاز به کد رهگیری:</span>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        insurance.requiresExtraCode 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {insurance.requiresExtraCode ? 'بله' : 'خیر'}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">نیاز به تصویر دفترچه:</span>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        insurance.requiresInsuranceBooklet 
                          ? 'bg-purple-100 text-purple-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {insurance.requiresInsuranceBooklet ? 'بله' : 'خیر'}
                      </span>
                    </div>

                    <div className="text-xs text-gray-500">
                      ایجاد شده: {new Date(insurance.createdAt).toLocaleDateString('fa-IR')}
                    </div>
                  </div>
                  
                  <div className="mt-4 flex justify-end space-x-2">
                    <button 
                      onClick={() => handleToggleStatus(insurance._id)}
                      className={`ml-2 px-2 py-1 rounded text-xs ${
                        insurance.active 
                          ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                    >
                      {insurance.active ? 'غیرفعال کردن' : 'فعال کردن'}
                    </button>
                    <button 
                      onClick={() => startEditing(insurance._id)}
                      className="ml-2 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200"
                    >
                      ویرایش
                    </button>
                    <button 
                      onClick={() => handleDeleteInsurance(insurance._id)}
                      className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200"
                    >
                      حذف
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Supplementary Insurance Management Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mt-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">مدیریت بیمه های تکمیلی</h1>
          <button 
            onClick={() => {
              setEditingSupplementaryInsurance(null);
              setShowSupplementaryInsuranceForm(!showSupplementaryInsuranceForm);
            }}
            className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            {showSupplementaryInsuranceForm && !editingSupplementaryInsurance ? 'انصراف' : 'افزودن بیمه تکمیلی جدید'}
          </button>
        </div>

        {supplementaryError && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
            <span className="block sm:inline">{supplementaryError}</span>
            <button
              onClick={() => setSupplementaryError(null)}
              className="absolute top-0 bottom-0 left-0 px-4 py-3"
            >
              <span className="sr-only">بستن</span>
              ✕
            </button>
          </div>
        )}
        
        {showSupplementaryInsuranceForm && (
          <div className="mb-8 bg-gray-50 p-6 rounded-lg">
            <h2 className="text-lg font-semibold mb-4">
              {editingSupplementaryInsurance !== null ? 'ویرایش بیمه تکمیلی' : 'افزودن بیمه تکمیلی جدید'}
            </h2>
            <form onSubmit={addOrUpdateSupplementaryInsurance}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">نام بیمه تکمیلی</label>
                  <input
                    type="text"
                    name="name"
                    value={currentSupplementaryInsurance?.name || ''}
                    onChange={handleSupplementaryInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                    placeholder="نام بیمه تکمیلی را وارد کنید"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">وضعیت</label>
                  <select
                    name="active"
                    value={currentSupplementaryInsurance?.active ? 'true' : 'false'}
                    onChange={handleSupplementaryInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="true">فعال</option>
                    <option value="false">غیرفعال</option>
                  </select>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-2">
                {editingSupplementaryInsurance !== null && (
                  <button
                    type="button"
                    onClick={cancelEditingSupplementaryInsurance}
                    className="ml-2 px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    انصراف
                  </button>
                )}
                <button
                  type="submit"
                  disabled={supplementarySubmitting}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                >
                  {supplementarySubmitting 
                    ? 'در حال ذخیره...' 
                    : editingSupplementaryInsurance !== null 
                      ? 'به‌روزرسانی بیمه تکمیلی' 
                      : 'افزودن بیمه تکمیلی'
                  }
                </button>
              </div>
            </form>
          </div>
        )}
        
        {supplementaryInsurances.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-500 text-lg">هیچ بیمه تکمیلی‌ای یافت نشد.</div>
            <button
              onClick={() => setShowSupplementaryInsuranceForm(true)}
              className="mt-4 bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700"
            >
              افزودن اولین بیمه تکمیلی
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {supplementaryInsurances.map(supplementaryInsurance => (
              <div 
                key={supplementaryInsurance._id} 
                className={`border rounded-lg shadow-sm overflow-hidden ${supplementaryInsurance.active ? '' : 'opacity-60'}`}
              >
                <div className="bg-purple-100 p-4">
                  <div className="flex justify-between items-start">
                    <h2 className="text-lg font-semibold">{supplementaryInsurance.name}</h2>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      supplementaryInsurance.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {supplementaryInsurance.active ? 'فعال' : 'غیرفعال'}
                    </span>
                  </div>
                </div>
                
                <div className="p-4">
                  <div className="space-y-3">
                    <div className="text-xs text-gray-500">
                      ایجاد شده: {new Date(supplementaryInsurance.createdAt).toLocaleDateString('fa-IR')}
                    </div>
                  </div>
                  
                  <div className="mt-4 flex justify-end space-x-2">
                    <button 
                      onClick={() => handleToggleSupplementaryInsuranceStatus(supplementaryInsurance._id)}
                      className={`ml-2 px-2 py-1 rounded text-xs ${
                        supplementaryInsurance.active 
                          ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                    >
                      {supplementaryInsurance.active ? 'غیرفعال کردن' : 'فعال کردن'}
                    </button>
                    <button 
                      onClick={() => startEditingSupplementaryInsurance(supplementaryInsurance._id)}
                      className="ml-2 px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs hover:bg-purple-200"
                    >
                      ویرایش
                    </button>
                    <button 
                      onClick={() => handleDeleteSupplementaryInsurance(supplementaryInsurance._id)}
                      className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200"
                    >
                      حذف
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default InsuranceManagement; 